import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconPhoto,
  IconPin,
  IconPinnedOff,
  IconPlaylist,
  IconPlus,
  IconRefresh,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import type {
  WallAd,
  WallDisplay,
  WallPlaylist,
  WallPlaylistRole,
} from '@techgitter/contracts';

import {
  approveWallDisplay,
  clearWallPin,
  clearWallPlaylist,
  createWallPlaylist,
  deleteWallDisplay,
  fetchWallAds,
  fetchWallDisplays,
  fetchWallPlaylists,
  pinWallAd,
  purgeWallAd,
  removeWallAd,
  removeWallPlaylist,
  reorderWallAds,
  revokeWallDisplay,
  updateWallAd,
  updateWallPlaylist,
  uploadWallAd,
} from '../services/wallAdminApi';
import { SecondaryToolbar, ToolbarButton, ToolbarSpacer } from '../components/toolbar';
import {
  DataTable,
  DataTableCard,
  DataTableCell,
  DataTableEmpty,
  DataTableHeadCell,
  DataTableRow,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX } from '../styles/designSystem';

/**
 * Reception wall-TV admin: approve the screens, manage what they loop, and pin
 * an image when you want one up right now.
 *
 * The approval flow is the part that needs explaining on screen, so the page
 * does. A wall display has no login — it enrols itself and shows a pairing
 * code, and approving is a matter of **matching that code** against what is on
 * the TV. Approving the wrong row would hand wall state to someone else's
 * browser, so the code is rendered prominently and the button copy says what
 * to check.
 */

const RELATIVE_THRESHOLDS: Array<[number, Intl.RelativeTimeFormatUnit]> = [
  [60, 'second'],
  [3600, 'minute'],
  [86400, 'hour'],
];

/** "3s ago" / "2m ago" / "—". Used for the last-seen heartbeat. */
function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const deltaSeconds = (then - Date.now()) / 1000;
  const magnitude = Math.abs(deltaSeconds);

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [limit, unit] of RELATIVE_THRESHOLDS) {
    if (magnitude < limit) {
      const divisor = limit === 60 ? 1 : limit === 3600 ? 60 : 3600;
      return formatter.format(Math.round(deltaSeconds / divisor), unit);
    }
  }
  return formatter.format(Math.round(deltaSeconds / 86400), 'day');
}

function statusTone(status: WallDisplay['status']) {
  if (status === 'approved') return 'success' as const;
  if (status === 'revoked') return 'error' as const;
  return 'warning' as const;
}

/**
 * A display is "live" if it polled within the last couple of minutes. The
 * server only writes last-seen about once a minute (a 2s poll must not become
 * a 2s write), so the window has to be generous — this is a heartbeat, not a
 * precise presence signal.
 */
const LIVE_WINDOW_MS = 150_000;

/**
 * What each role means on the wall. Roles are the whole point of a playlist —
 * they wire it into automatic selection — so the admin says so rather than
 * showing a bare enum value.
 */
const ROLE_OPTIONS: Array<{ value: WallPlaylistRole | ''; label: string; hint: string }> = [
  { value: '', label: 'Manual only', hint: 'Only shows when someone picks it' },
  { value: 'default', label: 'Default', hint: 'Runs when nothing else applies' },
  {
    value: 'residential',
    label: 'Residential',
    hint: 'Customer left the business name blank',
  },
  {
    value: 'business',
    label: 'Business',
    hint: 'Customer filled in a business name',
  },
];

const roleLabel = (role: WallPlaylist['role']): string =>
  ROLE_OPTIONS.find((o) => (o.value || null) === role)?.label ?? 'Manual only';

function isLive(display: WallDisplay): boolean {
  if (!display.lastSeenAt) return false;
  const seen = Date.parse(display.lastSeenAt);
  return !Number.isNaN(seen) && Date.now() - seen < LIVE_WINDOW_MS;
}

export const WallDisplayAdminPage: React.FC = () => {
  const [displays, setDisplays] = useState<WallDisplay[]>([]);
  const [ads, setAds] = useState<WallAd[]>([]);
  const [playlists, setPlaylists] = useState<WallPlaylist[]>([]);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  // Which rotation a new upload lands in. '' = leave unassigned, which the
  // default playlist adopts.
  const [uploadTarget, setUploadTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [confirmRevoke, setConfirmRevoke] = useState<WallDisplay | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WallDisplay | null>(null);
  // Deleting an ad for good is the one action on this page with nothing behind
  // it — the row and the image file both go — so it always asks first.
  const [confirmPurgeAd, setConfirmPurgeAd] = useState<WallAd | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Dwell is edited in minutes, so the field holds a string while someone is
  // mid-type ("1", "1." on the way to "1.5") rather than a coerced number.
  const [dwellDraft, setDwellDraft] = useState<Record<string, string>>({});

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const notify = useCallback(
    (message: string, severity: 'success' | 'error' = 'success') =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const [displayRows, adRows, playlistRows] = await Promise.all([
        fetchWallDisplays(signal),
        fetchWallAds(signal, { includeInactive: true }),
        fetchWallPlaylists(signal, { includeInactive: true }),
      ]);
      setDisplays(displayRows);
      setAds(adRows);
      setPlaylists(playlistRows);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setLoadError(err instanceof Error ? err.message : 'Failed to load wall config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const replaceDisplay = (updated: WallDisplay) =>
    setDisplays((current) => current.map((d) => (d.id === updated.id ? updated : d)));

  const replaceAd = (updated: WallAd) =>
    setAds((current) => current.map((a) => (a.id === updated.id ? updated : a)));

  // -------------------------------------------------------------------------
  // Displays
  // -------------------------------------------------------------------------

  const handleApprove = async (display: WallDisplay) => {
    setBusyId(display.id);
    try {
      replaceDisplay(await approveWallDisplay(display.id));
      notify('Display approved — the wall comes up within a couple of seconds');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to approve', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setConfirmSubmitting(true);
    try {
      replaceDisplay(await revokeWallDisplay(confirmRevoke.id));
      notify('Display revoked');
      setConfirmRevoke(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to revoke', 'error');
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setConfirmSubmitting(true);
    try {
      await deleteWallDisplay(confirmDelete.id);
      setDisplays((current) => current.filter((d) => d.id !== confirmDelete.id));
      notify('Enrolment deleted');
      setConfirmDelete(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete', 'error');
    } finally {
      setConfirmSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Ads
  // -------------------------------------------------------------------------

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const created = await uploadWallAd(file, {
        playlistId: uploadTarget || undefined,
      });
      setAds((current) => [...current, created]);
      notify(`“${created.name}” added to the rotation`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const activeAds = useMemo(
    () => ads.filter((a) => a.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [ads],
  );

  /**
   * Move an ad one place in the rotation. The server takes a full ordering, so
   * the new sequence is computed here and sent whole — that also means a stale
   * page can't reshuffle rows it never saw.
   */
  const handleMove = async (ad: WallAd, direction: -1 | 1) => {
    const ordered = [...activeAds];
    const from = ordered.findIndex((a) => a.id === ad.id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    [ordered[from], ordered[to]] = [ordered[to], ordered[from]];

    setBusyId(ad.id);
    try {
      setAds(await reorderWallAds(ordered.map((a) => a.id)));
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to reorder', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleTogglePostCheckIn = async (ad: WallAd) => {
    setBusyId(ad.id);
    try {
      const updated = await updateWallAd(ad.id, { isPostCheckIn: !ad.isPostCheckIn });
      // Setting the flag clears it everywhere else server-side, so re-read
      // rather than patching one row into stale local state.
      setAds(await fetchWallAds(undefined, { includeInactive: true }));
      notify(
        updated.isPostCheckIn
          ? 'Shown briefly after each check-in'
          : 'The rotation now resumes straight after a check-in',
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Commit an edited dwell. The server owns the floor and rejects anything
   * under it with a readable message, so this only guards against sending
   * nonsense — it deliberately doesn't duplicate the rule.
   */
  const handleDwellCommit = async (ad: WallAd) => {
    const draft = dwellDraft[ad.id];
    if (draft === undefined) return;

    const minutes = Number(draft);
    setDwellDraft(({ [ad.id]: _dropped, ...rest }) => rest);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      notify('Enter how many minutes the image should stay up', 'error');
      return;
    }

    const dwellMs = Math.round(minutes * 60_000);
    if (dwellMs === ad.dwellMs) return;

    setBusyId(ad.id);
    try {
      replaceAd(await updateWallAd(ad.id, { dwellMs }));
      notify(`“${ad.name}” now shows for ${minutes} min`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveAd = async (ad: WallAd) => {
    setBusyId(ad.id);
    try {
      replaceAd(await removeWallAd(ad.id));
      notify(`“${ad.name}” taken off the wall`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to remove', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRestoreAd = async (ad: WallAd) => {
    setBusyId(ad.id);
    try {
      replaceAd(await updateWallAd(ad.id, { isActive: true }));
      notify(`“${ad.name}” back in the rotation`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to restore', 'error');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Delete an ad permanently — the row and the image file in Blob Storage.
   * Only reachable for an ad already taken off the wall, so "gone for good" is
   * a second decision rather than one click away from the rotation.
   */
  const handlePurgeAd = async () => {
    if (!confirmPurgeAd) return;
    const ad = confirmPurgeAd;
    setConfirmSubmitting(true);
    try {
      await purgeWallAd(ad.id);
      setAds((current) => current.filter((a) => a.id !== ad.id));
      notify(`“${ad.name}” deleted`);
      setConfirmPurgeAd(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete', 'error');
    } finally {
      setConfirmSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Playlists
  // -------------------------------------------------------------------------

  const activePlaylists = useMemo(
    () => playlists.filter((p) => p.isActive),
    [playlists],
  );

  const handleCreatePlaylist = async () => {
    const name = newPlaylistName.trim();
    if (!name) return;
    setBusyId('new-playlist');
    try {
      const created = await createWallPlaylist({ name });
      setPlaylists((current) => [...current, created]);
      setNewPlaylistName('');
      notify(`“${created.name}” created`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to create', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handlePlaylistRole = async (
    playlist: WallPlaylist,
    role: WallPlaylistRole | '',
  ) => {
    setBusyId(playlist.id);
    try {
      await updateWallPlaylist(playlist.id, { role: role || null });
      // A role is exclusive, so claiming one releases it elsewhere — re-read
      // rather than patching a single row into now-stale local state.
      setPlaylists(await fetchWallPlaylists(undefined, { includeInactive: true }));
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRetirePlaylist = async (playlist: WallPlaylist) => {
    setBusyId(playlist.id);
    try {
      const updated = await removeWallPlaylist(playlist.id);
      setPlaylists((current) =>
        current.map((p) => (p.id === updated.id ? updated : p)),
      );
      notify(`“${playlist.name}” retired`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to retire', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleReinstatePlaylist = async (playlist: WallPlaylist) => {
    setBusyId(playlist.id);
    try {
      const updated = await updateWallPlaylist(playlist.id, { isActive: true });
      setPlaylists((current) =>
        current.map((p) => (p.id === updated.id ? updated : p)),
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to reinstate', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleMoveAdToPlaylist = async (ad: WallAd, playlistId: string) => {
    setBusyId(ad.id);
    try {
      replaceAd(await updateWallAd(ad.id, { playlistId: playlistId || null }));
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to move', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleClearSwitch = async () => {
    try {
      await clearWallPlaylist();
      notify('Back to automatic playlist selection');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to clear', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Pin
  // -------------------------------------------------------------------------

  const handlePin = async (ad: WallAd) => {
    setBusyId(ad.id);
    try {
      await pinWallAd(ad.id);
      notify(`“${ad.name}” pinned — clears on the next check-in, or in an hour`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to pin', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleClearPin = async () => {
    try {
      await clearWallPin();
      notify('Pin cleared — back to the rotation');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to clear the pin', 'error');
    }
  };

  const pendingCount = displays.filter((d) => d.status === 'pending').length;

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSpacer />
        <ToolbarButton icon={IconPlaylist} onClick={handleClearSwitch}>
          Clear playlist switch
        </ToolbarButton>
        <ToolbarButton icon={IconPinnedOff} onClick={handleClearPin}>
          Clear pin
        </ToolbarButton>
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton
          variant="primary"
          icon={IconUpload}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : 'Add image'}
        </ToolbarButton>
      </SecondaryToolbar>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleUpload}
        style={{ display: 'none' }}
      />

      <Box sx={{ p: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {pendingCount > 0 && (
              <Alert severity="info">
                {pendingCount === 1 ? 'A display is' : `${pendingCount} displays are`}{' '}
                waiting for approval. Check the code shown on the TV matches the
                pairing code below before approving.
              </Alert>
            )}

            <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: '10px 16px',
                  borderBottom: '0.5px solid #e2eaf3',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1a1a1a',
                }}
              >
                Displays
              </Box>
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Name</DataTableHeadCell>
                    <DataTableHeadCell>Pairing code</DataTableHeadCell>
                    <DataTableHeadCell>Status</DataTableHeadCell>
                    <DataTableHeadCell>Pinned IP</DataTableHeadCell>
                    <DataTableHeadCell>Last seen</DataTableHeadCell>
                    <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {displays.length === 0 ? (
                    <DataTableEmpty colSpan={6}>
                      No displays yet. Open /display on the wall TV and it will appear
                      here for approval.
                    </DataTableEmpty>
                  ) : (
                    displays.map((display) => (
                      <DataTableRow key={display.id}>
                        <DataTableCell bold>{display.name || 'Unnamed display'}</DataTableCell>
                        <DataTableCell>
                          <span
                            style={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              letterSpacing: '0.12em',
                              fontSize: 13,
                              color: '#1a1a1a',
                            }}
                          >
                            {display.pairingCode || '—'}
                          </span>
                        </DataTableCell>
                        <DataTableCell>
                          <Chip tone={statusTone(display.status)}>
                            {display.status || 'unknown'}
                          </Chip>
                        </DataTableCell>
                        <DataTableCell>
                          {display.status === 'approved' && display.pinnedIp
                            ? display.pinnedIp
                            : display.requestedIp || '—'}
                        </DataTableCell>
                        <DataTableCell>
                          <Tooltip title={display.lastSeenAt || 'never'}>
                            <span style={{ color: isLive(display) ? '#0D7A56' : '#888' }}>
                              {relativeTime(display.lastSeenAt)}
                            </span>
                          </Tooltip>
                        </DataTableCell>
                        <DataTableCell align="right">
                          {display.status !== 'approved' ? (
                            <Tooltip title="Check this code matches the TV, then approve">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={busyId === display.id}
                                  onClick={() => handleApprove(display)}
                                  startIcon={<IconCheck size={14} stroke={1.5} />}
                                >
                                  Approve
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Re-approve — repins to the display's current IP">
                              <span>
                                <Button
                                  size="small"
                                  disabled={busyId === display.id}
                                  onClick={() => handleApprove(display)}
                                >
                                  Repin
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {display.status === 'approved' && (
                            <Tooltip title="Revoke — the wall goes blank within a couple of seconds">
                              <IconButton
                                size="small"
                                onClick={() => setConfirmRevoke(display)}
                              >
                                <IconX size={16} stroke={1.5} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete this enrolment">
                            <IconButton size="small" onClick={() => setConfirmDelete(display)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </DataTableCell>
                      </DataTableRow>
                    ))
                  )}
                </tbody>
              </DataTable>
            </Box>

            <DataTableCard>
              <Box
                sx={{
                  p: '10px 16px',
                  borderBottom: '0.5px solid #e2eaf3',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
                  Playlists
                </Box>
                <input
                  placeholder="New playlist name"
                  aria-label="New playlist name"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreatePlaylist();
                  }}
                  style={{
                    height: 28,
                    padding: '0 8px',
                    fontSize: 13,
                    border: '0.5px solid #d0dcea',
                    borderRadius: 6,
                    background: '#fff',
                    width: 180,
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disabled={!newPlaylistName.trim() || busyId === 'new-playlist'}
                  onClick={handleCreatePlaylist}
                  startIcon={<IconPlus size={14} stroke={1.5} />}
                >
                  Add
                </Button>
              </Box>
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Name</DataTableHeadCell>
                    <DataTableHeadCell>Used when</DataTableHeadCell>
                    <DataTableHeadCell align="right">Images</DataTableHeadCell>
                    <DataTableHeadCell>Status</DataTableHeadCell>
                    <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {playlists.length === 0 ? (
                    <DataTableEmpty colSpan={5}>
                      No playlists yet. Without one, every image plays in a single
                      rotation.
                    </DataTableEmpty>
                  ) : (
                    playlists.map((playlist) => (
                      <DataTableRow key={playlist.id}>
                        <DataTableCell bold>{playlist.name}</DataTableCell>
                        <DataTableCell>
                          {playlist.isActive ? (
                            <select
                              aria-label={`Used when, for ${playlist.name}`}
                              disabled={busyId === playlist.id}
                              value={playlist.role ?? ''}
                              onChange={(e) =>
                                handlePlaylistRole(
                                  playlist,
                                  e.target.value as WallPlaylistRole | '',
                                )
                              }
                              style={{
                                  height: 28,
                                  padding: '0 6px',
                                  fontSize: 13,
                                  color: '#1a1a1a',
                                  border: '0.5px solid #d0dcea',
                                  borderRadius: 6,
                                  background: '#fff',
                                  maxWidth: 160,
                                }}
                            >
                              {ROLE_OPTIONS.map((option) => (
                                <option key={option.label} value={option.value}>
                                  {option.label} — {option.hint}
                                </option>
                              ))}
                            </select>
                          ) : (
                            roleLabel(playlist.role)
                          )}
                        </DataTableCell>
                        <DataTableCell align="right">
                          {ads.filter((a) => a.playlistId === playlist.id).length}
                        </DataTableCell>
                        <DataTableCell>
                          <Chip tone={playlist.isActive ? 'success' : 'neutral'}>
                            {playlist.isActive ? 'Active' : 'Retired'}
                          </Chip>
                        </DataTableCell>
                        <DataTableCell align="right">
                          {playlist.isActive ? (
                            <Tooltip title="Retire — its images stay linked, so reinstating restores it">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busyId === playlist.id}
                                  onClick={() => handleRetirePlaylist(playlist)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Button
                              size="small"
                              disabled={busyId === playlist.id}
                              onClick={() => handleReinstatePlaylist(playlist)}
                            >
                              Reinstate
                            </Button>
                          )}
                        </DataTableCell>
                      </DataTableRow>
                    ))
                  )}
                </tbody>
              </DataTable>
            </DataTableCard>

            <DataTableCard>
              <Box
                sx={{
                  p: '10px 16px',
                  borderBottom: '0.5px solid #e2eaf3',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
                  Images
                </Box>
                {activePlaylists.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ fontSize: 12, color: '#888' }}>Upload into</Box>
                    <select
                      aria-label="Playlist for new uploads"
                      value={uploadTarget}
                      onChange={(e) => setUploadTarget(e.target.value)}
                      style={{
                                  height: 28,
                                  padding: '0 6px',
                                  fontSize: 13,
                                  color: '#1a1a1a',
                                  border: '0.5px solid #d0dcea',
                                  borderRadius: 6,
                                  background: '#fff',
                                  maxWidth: 160,
                                }}
                    >
                      <option value="">Unassigned</option>
                      {activePlaylists.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </select>
                  </Box>
                )}
              </Box>
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Image</DataTableHeadCell>
                    <DataTableHeadCell>Name</DataTableHeadCell>
                    <DataTableHeadCell>Playlist</DataTableHeadCell>
                    <DataTableHeadCell align="right">Minutes on screen</DataTableHeadCell>
                    <DataTableHeadCell>After check-in</DataTableHeadCell>
                    <DataTableHeadCell>Status</DataTableHeadCell>
                    <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {ads.length === 0 ? (
                    <DataTableEmpty colSpan={7}>
                      No images yet. The wall shows a black screen until you add one.
                    </DataTableEmpty>
                  ) : (
                    ads.map((ad) => {
                      const position = activeAds.findIndex((a) => a.id === ad.id);
                      return (
                        <DataTableRow key={ad.id}>
                          <DataTableCell>
                            {ad.url ? (
                              <img
                                src={ad.url}
                                alt=""
                                style={{
                                  width: 72,
                                  height: 40,
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                  border: '0.5px solid #e2eaf3',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <IconPhoto size={18} stroke={1.5} color="#bbb" />
                            )}
                          </DataTableCell>
                          <DataTableCell bold>{ad.name}</DataTableCell>
                          <DataTableCell>
                            {ad.isActive && activePlaylists.length > 0 ? (
                              <select
                                aria-label={`Playlist for ${ad.name}`}
                                disabled={busyId === ad.id}
                                value={ad.playlistId ?? ''}
                                onChange={(e) =>
                                  handleMoveAdToPlaylist(ad, e.target.value)
                                }
                                style={{
                                  height: 28,
                                  padding: '0 6px',
                                  fontSize: 13,
                                  color: '#1a1a1a',
                                  border: '0.5px solid #d0dcea',
                                  borderRadius: 6,
                                  background: '#fff',
                                  maxWidth: 160,
                                }}
                              >
                                {/* Unassigned is a real state, not a blank: the
                                    default rotation adopts these, which is what
                                    stops a first playlist blanking the wall. */}
                                <option value="">Unassigned (plays in default)</option>
                                {activePlaylists.map((playlist) => (
                                  <option key={playlist.id} value={playlist.id}>
                                    {playlist.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              playlists.find((p) => p.id === ad.playlistId)?.name ?? '—'
                            )}
                          </DataTableCell>
                          <DataTableCell align="right">
                            {ad.isActive ? (
                              <input
                                type="number"
                                min={0}
                                step={0.5}
                                inputMode="decimal"
                                aria-label={`Minutes on screen for ${ad.name}`}
                                disabled={busyId === ad.id}
                                value={
                                  dwellDraft[ad.id] ??
                                  String(Number((ad.dwellMs / 60_000).toFixed(2)))
                                }
                                onChange={(e) =>
                                  setDwellDraft((current) => ({
                                    ...current,
                                    [ad.id]: e.target.value,
                                  }))
                                }
                                onBlur={() => handleDwellCommit(ad)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.currentTarget.blur();
                                  if (e.key === 'Escape') {
                                    setDwellDraft(
                                      ({ [ad.id]: _dropped, ...rest }) => rest,
                                    );
                                    e.currentTarget.blur();
                                  }
                                }}
                                style={{
                                  width: 68,
                                  height: 28,
                                  padding: '0 8px',
                                  textAlign: 'right',
                                  fontSize: 13,
                                  color: '#1a1a1a',
                                  border: '0.5px solid #d0dcea',
                                  borderRadius: 6,
                                  background: '#fff',
                                }}
                              />
                            ) : (
                              Number((ad.dwellMs / 60_000).toFixed(2))
                            )}
                          </DataTableCell>
                          <DataTableCell>
                            {ad.isPostCheckIn ? <Chip tone="info">Shown</Chip> : '—'}
                          </DataTableCell>
                          <DataTableCell>
                            <Chip tone={ad.isActive ? 'success' : 'neutral'}>
                              {ad.isActive ? 'In rotation' : 'Removed'}
                            </Chip>
                          </DataTableCell>
                          <DataTableCell align="right">
                            {ad.isActive && (
                              <>
                                <Tooltip title="Move earlier">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={position <= 0 || busyId === ad.id}
                                      onClick={() => handleMove(ad, -1)}
                                    >
                                      <IconArrowUp size={16} stroke={1.5} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Move later">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={
                                        position < 0 ||
                                        position >= activeAds.length - 1 ||
                                        busyId === ad.id
                                      }
                                      onClick={() => handleMove(ad, 1)}
                                    >
                                      <IconArrowDown size={16} stroke={1.5} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Put this on the wall now">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={busyId === ad.id}
                                      onClick={() => handlePin(ad)}
                                    >
                                      <IconPin size={16} stroke={1.5} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    ad.isPostCheckIn
                                      ? 'Stop showing this after a check-in'
                                      : 'Show this briefly after each check-in'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={busyId === ad.id}
                                      onClick={() => handleTogglePostCheckIn(ad)}
                                    >
                                      <IconCheck
                                        size={16}
                                        stroke={1.5}
                                        color={ad.isPostCheckIn ? '#4A90D9' : undefined}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                            {ad.isActive ? (
                              <Tooltip title="Take off the wall">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busyId === ad.id}
                                    onClick={() => handleRemoveAd(ad)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <>
                                <Button
                                  size="small"
                                  disabled={busyId === ad.id}
                                  onClick={() => handleRestoreAd(ad)}
                                >
                                  Restore
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={busyId === ad.id}
                                  onClick={() => setConfirmPurgeAd(ad)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </DataTableCell>
                        </DataTableRow>
                      );
                    })
                  )}
                </tbody>
              </DataTable>
            </DataTableCard>
          </>
        )}
      </Box>

      <Dialog
        open={confirmRevoke !== null}
        onClose={() => !confirmSubmitting && setConfirmRevoke(null)}
      >
        <DialogTitle>Revoke display</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{confirmRevoke?.name || 'This display'}</strong> will drop back to
            its holding screen within a couple of seconds and stay there. Its enrolment
            is kept, so its device key can never be approved again — the TV has to
            re-enrol from scratch.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(null)} disabled={confirmSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
            disabled={confirmSubmitting}
          >
            {confirmSubmitting ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelete !== null}
        onClose={() => !confirmSubmitting && setConfirmDelete(null)}
      >
        <DialogTitle>Delete enrolment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{confirmDelete?.name || 'this enrolment'}</strong>? Best for
            tidying up a duplicate request that was never approved. For a screen that
            was ever live, revoke instead — that keeps the audit trail and blocks the
            old device key for good.
          </DialogContentText>
          {confirmDelete?.status === 'approved' && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              This display is live. Deleting it leaves the TV showing its holding
              screen with a pairing code that won&rsquo;t appear in this list. It
              re-enrols itself after about 30 minutes — to fix it sooner, load{' '}
              <strong>/display?reset=1</strong> on the TV.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={confirmSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={confirmSubmitting}
          >
            {confirmSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmPurgeAd !== null}
        onClose={() => !confirmSubmitting && setConfirmPurgeAd(null)}
      >
        <DialogTitle>Delete image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{confirmPurgeAd?.name || 'this image'}</strong> for good?
            The image file is removed from storage as well as the record, so there is
            nothing left to restore. Leave it removed instead if you might want it back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPurgeAd(null)} disabled={confirmSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handlePurgeAd}
            disabled={confirmSubmitting}
          >
            {confirmSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WallDisplayAdminPage;
